import { motion } from 'framer-motion'
import React from 'react'
import { STAGE_COLORS, STAGE_ORDER } from './career-path-constants'

interface StageProgressionBarProps {
    currentStage: string
    /** Set of stage values that the employee actually went through */
    passedStages: Set<string>
    stages: { value: string; label: string }[]
}

const StageProgressionBar: React.FC<StageProgressionBarProps> = ({
    currentStage,
    passedStages,
    stages,
}) => {
    const currentIndex = STAGE_ORDER.indexOf(currentStage)
    const totalStages = stages.length

    return (
        <div className="mb-6 mx-auto" style={{ width: '80%' }}>
            <svg
                width="100%"
                height="48"
                viewBox="0 0 400 48"
                preserveAspectRatio="xMidYMid meet"
            >
                {/* Background track — draw segments only between passed stages */}
                <line
                    x1="20"
                    y1="24"
                    x2="380"
                    y2="24"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                    strokeLinecap="round"
                />
                {/* Progress fill */}
                <motion.line
                    x1="20"
                    y1="24"
                    x2={20 + (currentIndex / (totalStages - 1)) * 360}
                    y2="24"
                    stroke={STAGE_COLORS[currentStage]?.dot || '#9ca3af'}
                    strokeWidth="3"
                    strokeLinecap="round"
                    initial={{ x2: 20 }}
                    animate={{
                        x2: 20 + (currentIndex / (totalStages - 1)) * 360,
                    }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                />
                {/* Stage nodes */}
                {stages.map((s, i) => {
                    const x = 20 + (i / (totalStages - 1)) * 360
                    const isActive = passedStages.has(s.value)
                    const isCurrent = i === currentIndex
                    const nodeColor = STAGE_COLORS[s.value]?.dot || '#9ca3af'

                    return (
                        <g key={s.value}>
                            {isCurrent && (
                                <motion.circle
                                    cx={x}
                                    cy={24}
                                    r={10}
                                    fill="none"
                                    stroke={nodeColor}
                                    strokeWidth="2"
                                    opacity={0.4}
                                    animate={{
                                        r: [10, 16, 10],
                                        opacity: [0.4, 0, 0.4],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                    }}
                                />
                            )}
                            <motion.circle
                                cx={x}
                                cy={24}
                                r={isCurrent ? 8 : 6}
                                fill={isActive ? nodeColor : '#d1d5db'}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                    delay: i * 0.12,
                                    type: 'spring',
                                    stiffness: 400,
                                    damping: 20,
                                }}
                            />
                            {isActive && (
                                <motion.circle
                                    cx={x - 1.5}
                                    cy={22.5}
                                    r={2}
                                    fill="white"
                                    opacity={0.4}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 0.4 }}
                                    transition={{
                                        delay: i * 0.12 + 0.2,
                                    }}
                                />
                            )}
                            <text
                                x={x}
                                y={44}
                                textAnchor="middle"
                                fontSize="10"
                                fill={isActive ? nodeColor : '#9ca3af'}
                                fontWeight={isCurrent ? 'bold' : 'normal'}
                            >
                                {s.label}
                            </text>
                        </g>
                    )
                })}
            </svg>
        </div>
    )
}

export default StageProgressionBar
